import { useState } from 'react';
import { Image, Space } from 'antd';
import { ProductElement } from '../../types/product';

// Image object structure from external provider
interface ProductImage {
  original?: string;
  '500'?: string;
  '300'?: string;
}

interface ImageGalleryProps {
  elements: ProductElement[];
  image?: ProductImage | string | null;
}

function isImageElement(el: ProductElement): boolean {
  const t = (el.type || '').toLowerCase();
  return t === 'image';
}

function is3dElement(el: ProductElement): boolean {
  const t = (el.type || '').toLowerCase();
  return t === '3d';
}

function isVideoElement(el: ProductElement): boolean {
  const t = (el.type || '').toLowerCase();
  return t === 'video';
}

export function ImageGallery({ elements, image }: ImageGalleryProps) {
  const elementImages = elements.filter(isImageElement);
  const threeDElements = elements.filter(is3dElement);
  const videoElements = elements.filter(isVideoElement);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [show3d, setShow3d] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  // Build combined images array, deduplicating by URL
  const seen = new Set<string>();
  const images: { url: string; thumbnail?: string; label: string; subtype?: string }[] = [];

  // Add product.image first (it's the primary/hero image)
  // Determine its subtype by matching against elements
  if (image) {
    const imageUrl = typeof image === 'string'
      ? image
      : (image.original || image['500'] || image['300'] || '');
    if (imageUrl) {
      seen.add(imageUrl);
      const matchingElement = elementImages.find(el => el.url === imageUrl);
      const heroSubtype = matchingElement?.subtype || 'product_photo';
      images.push({ url: imageUrl, label: 'Product image', subtype: heroSubtype });
    }
  }

  // Add element images, skipping duplicates
  elementImages.forEach((el, i) => {
    if (!seen.has(el.url)) {
      seen.add(el.url);
      const subtype = el.subtype || undefined;
      const typeLabel = subtype === 'product_photo' ? 'Product' : subtype === 'lifestyle' ? 'Lifestyle' : '';
      const name = el.propertykey || `Image ${i + 1}`;
      images.push({
        url: el.url,
        thumbnail: el.thumbnail ?? undefined,
        label: typeLabel ? `${typeLabel}: ${name}` : name,
        subtype,
      });
    }
  });

  const has3d = threeDElements.length > 0;
  const hasVideo = videoElements.length > 0;

  if (images.length === 0 && !has3d && !hasVideo) {
    return (
      <div
        style={{
          width: '100%',
          height: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f5f5f5',
          borderRadius: 8,
        }}
      >
        <span style={{ color: '#999' }}>No images available</span>
      </div>
    );
  }

  const selectedImage = images[selectedIndex];

  return (
    <div>
      {/* Main display area */}
      <div
        style={{
          width: '100%',
          height: 350,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f5f5f5',
          borderRadius: 8,
          marginBottom: 12,
          overflow: 'hidden',
        }}
      >
        {show3d && has3d ? (
          <iframe
            src={threeDElements[0].url}
            title="3D View"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              borderRadius: 8,
            }}
            allowFullScreen
          />
        ) : showVideo && hasVideo ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxHeight: '100%' }}>
            <video
              src={videoElements[0].url}
              controls
              autoPlay
              muted
              loop
              style={{ maxHeight: 310, maxWidth: '100%', borderRadius: 8 }}
            />
            <span style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
              {videoElements[0].propertykey || '360 Video'}
            </span>
          </div>
        ) : selectedImage ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxHeight: '100%' }}>
            <Image
              src={selectedImage.url}
              alt={selectedImage.label}
              style={{ maxHeight: 310, maxWidth: '100%', objectFit: 'contain' }}
              fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect fill='%23f0f0f0' width='200' height='200'/%3E%3Ctext x='100' y='100' font-family='Arial' font-size='14' fill='%23999' text-anchor='middle' dy='.3em'%3EImage not available%3C/text%3E%3C/svg%3E"
            />
            <span style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
              {selectedImage.label}
            </span>
          </div>
        ) : null}
      </div>

      {/* Thumbnail strip - always show if there are images, 3D, or video */}
      {(images.length > 0 || has3d || hasVideo) && (
        <Space wrap size={8}>
          {images.map((img, index) => (
            <div
              key={index}
              onClick={() => {
                setSelectedIndex(index);
                setShow3d(false);
                setShowVideo(false);
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                gap: 2,
              }}
            >
              <div
                style={{
                  width: 60,
                  height: 60,
                  border:
                    index === selectedIndex && !show3d
                      ? '2px solid #1677ff'
                      : '1px solid #d9d9d9',
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#fafafa',
                }}
              >
                <img
                  src={img.thumbnail || img.url}
                  alt={img.label}
                  style={{
                    maxWidth: 54,
                    maxHeight: 54,
                    objectFit: 'contain',
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
              <span style={{
                fontSize: 9,
                color: img.subtype === 'product_photo' ? '#1677ff' : '#999',
                maxWidth: 64,
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {img.subtype === 'product_photo' ? 'Product' : img.subtype === 'lifestyle' ? 'Lifestyle' : ''}
              </span>
            </div>
          ))}
          {hasVideo && (
            <div
              onClick={() => { setShowVideo(true); setShow3d(false); }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                gap: 2,
              }}
            >
              <div
                style={{
                  width: 60,
                  height: 60,
                  border: showVideo
                    ? '2px solid #1677ff'
                    : '1px solid #d9d9d9',
                  borderRadius: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: showVideo ? '#e6f4ff' : '#fafafa',
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#1677ff',
                }}
                title="360 Video"
              >
                360°
              </div>
              <span style={{ fontSize: 9, color: '#1677ff' }}>Video</span>
            </div>
          )}
          {has3d && (
            <div
              onClick={() => { setShow3d(true); setShowVideo(false); }}
              style={{
                width: 60,
                height: 60,
                border: show3d
                  ? '2px solid #1677ff'
                  : '1px solid #d9d9d9',
                borderRadius: 4,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: show3d ? '#e6f4ff' : '#fafafa',
                fontSize: 11,
                fontWeight: 600,
                color: '#1677ff',
              }}
              title="3D View"
            >
              3D
            </div>
          )}
        </Space>
      )}
    </div>
  );
}
